import { cloneDeep, concat } from 'lodash';
import { EventDrivenTag, FilterType, IssuersFilterOption } from '../../models';
import { EVENT_DRIVEN_REPORT, EVENT_DRIVEN_TITLE_CONTAINS } from './constants';

export const DATE_RELATIVE_RANGE_URL_KEY = 'dateRelativeRange';
export const START_DATE_URL_KEY = 'startDate';
export const END_DATE_URL_KEY = 'endDate';
export const TITLE_CONTAINS_URL_KEY = 'titleContains';
export const KEYWORD_URL_KEY = 'keyword';
export const ONBOARDING_COMPLETED_URL_KEY = 'onboarding-completed';
export const CONTENT_TYPE_FILTER_URL_KEY = 'type';

export const getHiddenCheckedKeys = (
  isDateSelected: boolean,
  filtersArray: HTMLDivElement[],
  filtersWrapperElement: HTMLDivElement,
  selectedTags: Record<string, string[]>
): number => {
  // filters are considered to be hidden if their top is below wrapper bottom
  const hiddenCheckedKeysCount = filtersArray
    .filter((filter) => filtersWrapperElement?.getBoundingClientRect().bottom < filter?.getBoundingClientRect().top)
    .map((hiddenFilter) => {
      const hiddenFilterName = hiddenFilter.getAttribute('data-filter-name');
      const checkedKeys = (hiddenFilterName && selectedTags[hiddenFilterName]) || [];
      return checkedKeys.length;
    })
    .reduce((keysCount, value) => {
      return keysCount + value;
    }, 0);
  return isDateSelected ? hiddenCheckedKeysCount + 1 : hiddenCheckedKeysCount;
};

export const convertFiltersToMap = (filters: string[][]): Record<string, string[]> => {
  return filters
    .filter(
      (filter) =>
        ![
          START_DATE_URL_KEY,
          END_DATE_URL_KEY,
          DATE_RELATIVE_RANGE_URL_KEY,
          ONBOARDING_COMPLETED_URL_KEY,
          TITLE_CONTAINS_URL_KEY,
          CONTENT_TYPE_FILTER_URL_KEY,
        ].includes(filter[0])
    )
    .reduce((acc, filter) => {
      const filterName = filter[0];
      const filterValue = filter[1];
      const oldValues = acc[filterName];
      acc[filterName] = oldValues && !oldValues.includes(filterValue) ? [...oldValues, filterValue] : [filterValue];
      return acc;
    }, {});
};

export const getApiFilterTypes = (filterTypes: IssuersFilterOption[]): FilterType[] => {
  return Object.values(FilterType).filter((filterType) => filterTypes.includes(filterType));
};

export const findTitleContains = (tags: Record<string, string[]>): string[] | undefined => {
  if (!tags[FilterType.source] && !tags[FilterType.covenants_source]) {
    return undefined;
  }
  const titleContainsArray = concat(tags[FilterType.source], tags[FilterType.covenants_source]).reduce((acc, id) => {
    const title = EVENT_DRIVEN_TITLE_CONTAINS[id];
    if (title) {
      return [...acc, title];
    }
    return acc;
  }, [] as string[]);

  return titleContainsArray.length === 0 ? undefined : titleContainsArray;
};

export const transformTags = (tags: Record<string, string[]>): Record<string, string[]> => {
  const copiedTags = cloneDeep(tags);
  if (copiedTags[FilterType.source]) {
    copiedTags[FilterType.source] = transformFilterIds(copiedTags[FilterType.source]);
  }
  if (copiedTags[FilterType.covenants_source]) {
    copiedTags[FilterType.covenants_source] = transformFilterIds(copiedTags[FilterType.covenants_source]);
  }

  return copiedTags;
};

const transformFilterIds = (ids: string[]): string[] => {
  let hasEventDrivenTags = false;
  const newIds = ids.filter((id) => {
    if (
      Object.values(EventDrivenTag)
        .map((value) => value.toString())
        .includes(id) ||
      id === EVENT_DRIVEN_REPORT
    ) {
      hasEventDrivenTags = true;
      return false;
    }
    return true;
  });

  if (hasEventDrivenTags) {
    return [...newIds, EVENT_DRIVEN_REPORT];
  }
  return newIds;
};
